import React, { useState } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { Link } from "react-router-dom";
import { ShieldAlert, X, ArrowRight } from "lucide-react";
import ConfirmDialog from "components/ConfirmDialog";
import TicketStatusPill from "components/Tickets/TicketStatusPill";

/**
 * DependencyBlockerNotice (SOUND-497)
 * -------------------------------------------------------
 * Shown when a done-category transition returns 409 with an open-blocker list.
 * Replaces the optimistic status flip for that case: the ticket stays put and
 * this notice explains why. Each blocker deep-links to its ticket. Internal
 * users get a GOVERNED OVERRIDE — a required reason, ConfirmDialog-gated, which
 * retries the status change with `override:{reason}` (the backend audits it and
 * rejects overrides from non-internal callers).
 */
export default function DependencyBlockerNotice({
  blockers = [],
  targetLabel,
  isInternal = false,
  submitting = false,
  onOverride,
  onDismiss,
}) {
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!blockers.length) return null;

  const n = blockers.length;

  return (
    <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 rounded-full bg-destructive/10 p-1.5">
          <ShieldAlert className="h-5 w-5 text-destructive" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {targetLabel ? `Can't move to ${targetLabel} yet` : "Blocked by dependencies"}
            </h3>
            <button
              type="button"
              onClick={onDismiss}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            This ticket is blocked by {n} unresolved {n === 1 ? "dependency" : "dependencies"}. Resolve
            {n === 1 ? " it" : " them"} first{isInternal ? ", or override with a reason." : "."}
          </p>

          <ul className="mt-3 space-y-1.5">
            {blockers.map((b) => (
              <li key={b.ticketId}>
                <Link
                  to={`/tickets/${b.ticketId}`}
                  className="group flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 hover:bg-muted"
                >
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">{ticketRef(b.number)}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{b.title}</span>
                  <TicketStatusPill status={b.status} statusCategory={b.statusCategory} />
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>

          {isInternal && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                className="input flex-1"
                placeholder="Override reason (required, audited)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-destructive shrink-0"
                disabled={!reason.trim() || submitting}
                onClick={() => setConfirmOpen(true)}
              >
                Override &amp; proceed
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Override dependency gate?"
        description={`This moves the ticket to ${
          targetLabel || "a done state"
        } despite ${n} open ${n === 1 ? "blocker" : "blockers"}. The override and your reason are recorded on the ticket's timeline.`}
        confirmLabel="Override"
        variant="destructive"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onOverride?.(reason.trim());
        }}
      />
    </div>
  );
}
